// ============================================================
// 近傍車両 + 停留所 → 表示用 NearbyState を集約
// + スポットライト (最も relevant なバス / 電車) を選ぶ
// ============================================================

import type { Entity, Fix, NearbyState, SpotlightItem } from '../types'
import { SpatialIndex } from './spatial-index'
import { AGGREGATOR } from '../config'
import { bearingDeg, bearingToCompass, distanceMeters } from '../util/distance'

export interface AggregatorOptions {
  radiusM: number
}

export class VehicleAggregator {
  private staticIndex = new SpatialIndex()
  private vehicles: Entity[] = []

  setStatic(entities: Entity[]): void {
    this.staticIndex.clear()
    this.staticIndex.add(entities)
  }

  setVehicles(entities: Entity[]): void {
    this.vehicles = entities
  }

  build(fix: Fix, opts: AggregatorOptions): NearbyState {
    const stops = this.staticIndex.query(fix, opts.radiusM)
    const vehiclesNearby = this.vehicles.filter(
      (v) => distanceMeters(fix, v) <= opts.radiusM,
    )
    const nearby = [...stops, ...vehiclesNearby]

    const buses = vehiclesNearby.filter((v) => v.kind === 'bus')
    const trains = vehiclesNearby.filter((v) => v.kind === 'train')

    return {
      fix,
      radiusM: opts.radiusM,
      nearby,
      spotlightBus: this.pickSpotlight(fix, buses, 'bus'),
      spotlightTrain: this.pickSpotlight(fix, trains, 'train'),
    }
  }

  // ───────── private ─────────

  private pickSpotlight(
    fix: Fix,
    candidates: Entity[],
    kind: 'bus' | 'train',
  ): SpotlightItem | null {
    if (candidates.length === 0) return null

    const heading = fix.heading
    let best: { item: SpotlightItem; score: number } | null = null

    for (const v of candidates) {
      const d = distanceMeters(fix, v)
      const θ = bearingDeg(fix, v)
      const eta = v.nextEta?.mins ?? AGGREGATOR.defaultEtaMins

      // スコア: 距離を強めに効かせる (近いほど大幅に優先)
      //   - 距離: 1 / (d/100)^2  (二乗で急傾斜)
      //   - ETA:  1 / max(ETA, 1)
      //   - 方位: ボーナス factor
      const distScore = 1 / Math.pow(Math.max(d / 100, 0.5), 2)
      const etaScore = 1 / Math.max(eta, 1)
      let s = distScore * etaScore
      if (heading != null) {
        const diff = Math.abs(((θ - heading + 540) % 360) - 180)
        if (diff <= AGGREGATOR.headingBonusHalfWidth) {
          s *= AGGREGATOR.headingBonusFactor
        }
      }

      // label = "都営バス 都02 渋谷駅前" のような形式を期待
      // 系統名と行き先を分けて表示するため、ラベルから抽出
      let line = v.label ?? (kind === 'bus' ? 'バス' : '電車')
      let destination = v.nextEta?.dest ?? ''
      if (v.nextEta?.line && v.nextEta.dest) {
        line = `都営バス ${v.nextEta.line}`
        destination = v.nextEta.dest
      }

      const item: SpotlightItem = {
        kind,
        line,
        destination,
        stop: v.nextEta?.line,
        mins: eta,
        distanceM: d,
        bearing: θ,
      }
      if (!best || s > best.score) best = { item, score: s }
    }

    return best?.item ?? null
  }
}

export function compassFromBearing(b: number): string {
  return bearingToCompass(b)
}
